import { ProgressBar } from '@core/components/ProgressBar';
import { Button } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './LabelWithSeveritySelector.css';

// Utility function to convert severity number to human-readable label
const getSeverityLabel = (severity: number, maxSeverity: number = 3): string => {
  if (maxSeverity <= 3) {
    // For 3-level system: 1=Low, 2=Medium, 3=High
    switch (severity) {
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      default:
        return severity.toString();
    }
  } else {
    // For larger scales, just use numbers
    return severity.toString();
  }
};

export interface LabelWithSeverity {
  labelId: string;
  severity: number;
}

export interface LabelOption {
  id: string;
  labelName: string;
  maxSeverity?: number;
}

interface LabelWithSeveritySelectorProps {
  availableLabels: LabelOption[];
  selectedLabels: LabelWithSeverity[];
  loading?: boolean;
  title?: string;
  emptyStateMessage?: string;
  severityMin?: number;
  defaultSeverity?: number;
  defaultMaxSeverity?: number;
  showSeverityDescription?: boolean;
  onChange: (labels: LabelWithSeverity[]) => void;
}

// eslint-disable-next-line complexity
export function LabelWithSeveritySelector({
  availableLabels,
  selectedLabels,
  loading,
  title = 'Labels',
  emptyStateMessage = 'No labels selected. Add labels to define criteria.',
  severityMin = 1,
  defaultSeverity = 2,
  defaultMaxSeverity = 3,
  showSeverityDescription = true,
  onChange,
}: LabelWithSeveritySelectorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [severity, setSeverity] = useState(defaultSeverity);

  // Get the max severity for the currently selected label
  const getMaxSeverityForLabel = (labelId: string): number => {
    const label = availableLabels.find(l => l.id === labelId);

    return label?.maxSeverity ?? defaultMaxSeverity;
  };

  // Get the max severity for display purposes
  const currentMaxSeverity = selectedLabelId ? getMaxSeverityForLabel(selectedLabelId) : defaultMaxSeverity;

  const unselectedLabels = availableLabels.filter(label => !selectedLabels.some(selected => selected.labelId === label.id));

  const handleAddLabel = () => {
    const maxSev = getMaxSeverityForLabel(selectedLabelId);

    if (selectedLabelId && selectedLabelId.trim() && severity >= severityMin && severity <= maxSev) {
      const newLabel: LabelWithSeverity = {
        labelId: selectedLabelId.trim(),
        severity: Math.min(severity, maxSev),
      };

      onChange([...selectedLabels, newLabel]);
      setSelectedLabelId('');
      setSeverity(defaultSeverity);
      setShowAddForm(false);
    }
  };

  const handleRemoveLabel = (labelId: string) => {
    onChange(selectedLabels.filter(label => label.labelId !== labelId));
  };

  const handleSeverityChange = (labelId: string, newSeverity: number) => {
    onChange(selectedLabels.map(label => (label.labelId === labelId ? { ...label, severity: newSeverity } : label)));
  };

  const getLabelName = (labelId: string) => availableLabels.find(label => label.id === labelId)?.labelName || labelId;

  const getSeverityColor = (severityValue: number, maxSeverity: number = currentMaxSeverity): 'warning' | 'success' | 'error' => {
    const percentage = ((severityValue - severityMin) / (maxSeverity - severityMin)) * 100;

    if (percentage < 33) return 'error';
    if (percentage < 66) return 'warning';

    return 'success';
  };

  if (loading) {
    return <div className={styles.loading}>Loading labels...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        {unselectedLabels.length > 0 && <Button onClick={() => setShowAddForm(!showAddForm)}>{showAddForm ? 'Cancel' : 'Add Label'}</Button>}
      </div>

      {/* Add Label Form */}
      {showAddForm && (
        <div className={styles.addForm}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="labelSelect">
              Select Label
            </label>
            <select className={styles.select} id="labelSelect" value={selectedLabelId} onChange={e => setSelectedLabelId(e.target.value)}>
              <option value="">Choose a label...</option>
              {unselectedLabels.map(label => (
                <option key={label.id} value={label.id}>
                  {label.labelName}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="severitySlider">
              Severity Threshold: {getSeverityLabel(severity, currentMaxSeverity)} ({severity}/{currentMaxSeverity})
            </label>
            <div className={styles.sliderContainer}>
              <span aria-hidden="true" className={styles.sliderLabel}>
                {severityMin}
              </span>
              <div className={styles.sliderWrapper}>
                <input
                  aria-describedby={showSeverityDescription ? 'severity-description' : undefined}
                  className={styles.slider}
                  id="severitySlider"
                  max={currentMaxSeverity}
                  min={severityMin}
                  type="range"
                  value={Math.min(severity, currentMaxSeverity)}
                  onChange={e => setSeverity(parseInt(e.target.value, 10))}
                />
                <div
                  aria-label={`Severity level: ${getSeverityLabel(severity, currentMaxSeverity)} (${severity} out of ${currentMaxSeverity})`}
                  className={styles.progressBarWrapper}
                  role="img"
                >
                  <ProgressBar
                    color={getSeverityColor(severity, currentMaxSeverity)}
                    size="small"
                    value={((Math.min(severity, currentMaxSeverity) - severityMin) / (currentMaxSeverity - severityMin)) * 100}
                  />
                </div>
              </div>
              <span aria-hidden="true" className={styles.sliderLabel}>
                {currentMaxSeverity}
              </span>
            </div>
          </div>

          <div className={styles.formActions}>
            <Button disabled={!selectedLabelId || !selectedLabelId.trim()} onClick={handleAddLabel}>
              Add Label
            </Button>
            <Button onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Selected Labels List */}
      {selectedLabels.length > 0 && (
        <div>
          <div className={styles.sectionTitle}>Selected Labels ({selectedLabels.length})</div>
          <div className={styles.labelsList}>
            {selectedLabels.map(label => {
              const labelMaxSeverity = getMaxSeverityForLabel(label.labelId);
              const displaySeverity = Math.min(label.severity, labelMaxSeverity);

              return (
                <div key={label.labelId} className={styles.labelItem}>
                  <div className={styles.labelInfo}>
                    <div className={styles.labelName}>{getLabelName(label.labelId)}</div>
                    <div className={styles.labelSeverity}>
                      Severity threshold: {getSeverityLabel(displaySeverity, labelMaxSeverity)} ({displaySeverity}/{labelMaxSeverity})
                    </div>
                  </div>
                  <div className={styles.labelControls}>
                    <div className={styles.severityControl}>
                      <input
                        aria-describedby={showSeverityDescription ? `severity-desc-${label.labelId}` : undefined}
                        aria-label={`Severity threshold for ${getLabelName(label.labelId)}: ${getSeverityLabel(displaySeverity, labelMaxSeverity)} (${displaySeverity} out of ${labelMaxSeverity})`}
                        className={styles.controlSlider}
                        max={labelMaxSeverity}
                        min={severityMin}
                        type="range"
                        value={displaySeverity}
                        onChange={e => handleSeverityChange(label.labelId, parseInt(e.target.value, 10))}
                      />
                      <div
                        aria-label={`Severity level: ${getSeverityLabel(displaySeverity, labelMaxSeverity)} (${displaySeverity} out of ${labelMaxSeverity})`}
                        className={styles.controlProgressBar}
                        role="img"
                      >
                        <ProgressBar
                          color={getSeverityColor(displaySeverity, labelMaxSeverity)}
                          size="compact"
                          value={((displaySeverity - severityMin) / (labelMaxSeverity - severityMin)) * 100}
                        />
                      </div>
                      <span aria-hidden="true" className={styles.severityValue}>
                        {displaySeverity}
                      </span>
                    </div>
                    <Button aria-label={`Remove ${getLabelName(label.labelId)} label`} onClick={() => handleRemoveLabel(label.labelId)}>
                      Remove
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedLabels.length === 0 && (
        <div aria-live="polite" className={styles.emptyState} role="status">
          {emptyStateMessage}
        </div>
      )}
    </div>
  );
}
